import useSWR from 'swr';
import { apiErrorMessage, backendClient } from '@/lib/api-client';
import {
  normalizeDraftDocumentDocxForEditor,
  normalizeDraftDocumentDocxForPreview,
} from '@/lib/draftDocxNormalizer';
export {
  normalizeDraftDocumentDocxForEditor,
  normalizeDraftDocumentDocxForPreview,
};

const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export type DraftDocumentVisibility = 'private' | 'matter_read' | 'matter_write';
export type DraftDocumentCreateSource = 'blank' | 'template';

export interface DraftDocumentContent {
  format: 'text_view';
  body?: string | null;
  blocks?: Array<Record<string, unknown>> | null;
}

export interface DraftDocumentNode {
  node_id: string;
  kind: string;
  content: DraftDocumentContent;
  table?: Record<string, unknown> | null;
}

export interface DraftDocumentOutlineItem {
  node_id: string;
  index: number;
  level: number;
  text: string;
}

export interface DraftDocumentVersionSummary {
  guid: string;
  version_number: number;
  artifact_format: string;
  artifact_mime_type: string | null;
  artifact_blob_name: string | null;
  created_at: string;
}

export interface DraftDocumentVersionHistoryOperation {
  guid: string;
  operation_type: string;
  target_node_id: string | null;
  anchor_node_id: string | null;
  note: string | null;
  content_preview: string | null;
  docx_patch_mode?: string | null;
  docx_patch_reason?: string | null;
  created_at: string;
}

export interface DraftDocumentVersionHistoryItem {
  guid: string;
  version_number: number;
  artifact_format: string;
  artifact_mime_type: string | null;
  artifact_blob_name: string | null;
  created_at: string;
  actor_type: 'user' | string;
  actor_user_id: number | null;
  actor_user_name: string | null;
  instruction_chat_id: number | null;
  operation_count: number;
  operations: DraftDocumentVersionHistoryOperation[];
  change_summary: string;
}

export interface DraftDocumentVersionContent {
  guid: string;
  title: string;
  version_guid: string;
  version_number: number;
  artifact_format: string;
  artifact_mime_type: string | null;
  artifact_blob_name: string | null;
  created_at: string;
  package_kind: string | null;
  package_schema_version: number | null;
  text_view_format: string | null;
  text_view_token_estimate: number | null;
  page_setup?: Record<string, unknown> | null;
  text_view_content: string;
  source_content: string;
  outline: DraftDocumentOutlineItem[];
  node_count: number;
  nodes: DraftDocumentNode[];
}

export interface DraftDocumentWorkingCopyCheckpoint {
  checkpoint_guid: string;
  source_version_number: number | null;
  checkpoint_reason: string;
  created_at: string;
}

export interface DraftDocumentWorkingCopyCheckpointResponse {
  document: DraftDocument;
  checkpoint: DraftDocumentWorkingCopyCheckpoint;
}

export interface DraftDocumentCollaborationState {
  room_name: string | null;
  seed_version_number: number | null;
  latest_checkpoint_guid: string | null;
  status: 'inactive' | 'active' | 'error' | string;
  dirty: boolean;
  state_vector: string | null;
  last_connected_at: string | null;
  last_checkpointed_at: string | null;
}

export interface DraftDocumentOwner {
  user_guid: string | null;
  user_id: number | null;
  name: string | null;
  email: string | null;
}

export interface DraftDocumentTemplateReference {
  template_guid: string;
  scope: 'system' | 'tenant' | 'matter' | 'user' | string;
  title: string;
}

export interface DraftDocumentChatReference {
  chat_guid: string;
  title: string | null;
}

export interface DraftDocumentFileReference {
  file_reference_guid: string;
  display_name: string;
  source: string;
  folder_guid: string | null;
  folder_name: string | null;
  folder_path: string | null;
}

export interface DraftDocument {
  guid: string;
  title: string;
  status: string;
  visibility: DraftDocumentVisibility;
  read_only: boolean;
  read_only_reason: string | null;
  file_name: string;
  file_extension: string;
  is_docx: boolean;
  editable: boolean;
  owner: DraftDocumentOwner | null;
  last_updated_by: DraftDocumentOwner | null;
  is_owner: boolean;
  can_write: boolean;
  file_format: string;
  editor_kind: string;
  can_delete: boolean;
  can_update_visibility: boolean;
  created_at: string;
  updated_at: string | null;
  latest_version: DraftDocumentVersionSummary | null;
  collaboration: DraftDocumentCollaborationState;
  package_kind: string | null;
  package_schema_version: number | null;
  text_view_format: string | null;
  text_view_token_estimate: number | null;
  page_setup?: Record<string, unknown> | null;
  outline: DraftDocumentOutlineItem[];
  node_count: number;
  nodes: DraftDocumentNode[];
  created_from_template: DraftDocumentTemplateReference | null;
  created_from_chat: DraftDocumentChatReference | null;
  file_reference: DraftDocumentFileReference | null;
}

export interface DraftDocumentTemplate {
  template_guid: string;
  scope: 'system' | 'tenant' | 'matter' | 'user' | string;
  can_manage: boolean;
  title: string;
  description: string | null;
  when_to_use: string | null;
  availability_region: string | null;
  availability_forum: string | null;
  file_format: string;
  file_name: string;
  is_system: boolean;
  size_bytes: number | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface CreateDraftDocumentTemplateRequest {
  scope: 'system' | 'tenant' | 'matter' | 'user';
  title: string;
  file: File;
  description?: string | null;
  when_to_use?: string | null;
  availability_region?: string | null;
  availability_forum?: string | null;
}

export interface UpdateDraftDocumentTemplateRequest {
  title?: string;
  description?: string | null;
  when_to_use?: string | null;
  availability_region?: string | null;
  availability_forum?: string | null;
}

export interface CreateDraftDocumentRequest {
  title: string;
  source: DraftDocumentCreateSource;
  template_guid?: string;
  folder_guid?: string;
}

interface DraftDocumentListResponse {
  documents: DraftDocument[];
}

interface DraftDocumentTemplateListResponse {
  templates: DraftDocumentTemplate[];
}

interface DraftDocumentVersionHistoryResponse {
  versions: DraftDocumentVersionHistoryItem[];
}

interface DraftDocumentRestoreVersionResponse {
  document: DraftDocument;
  version_number: number;
}

const fetcher = async (url: string) => {
  const response = await backendClient.get(url);
  return response.data;
};

export const useDraftDocuments = (
  matterGuid: string | undefined,
  options: { owner?: 'mine' | 'shared' | null } = {},
) => {
  const params = new URLSearchParams();
  if (options.owner) params.set('owner', options.owner);
  const query = params.toString();
  const { data, error, isLoading, mutate } = useSWR<DraftDocumentListResponse>(
    matterGuid ? `/matter/${matterGuid}/drafts${query ? `?${query}` : ''}` : null,
    fetcher,
  );

  return {
    draftDocuments: data?.documents ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useDraftDocumentTemplates = (
  matterGuid: string | undefined,
  options: { query?: string; limit?: number; scopes?: string[] } = {},
) => {
  const params = new URLSearchParams();
  if (options.query) params.set('query', options.query);
  if (options.limit) params.set('limit', String(options.limit));
  for (const scope of options.scopes ?? []) {
    params.append('scope', scope);
  }
  const query = params.toString();
  const { data, error, isLoading, mutate } = useSWR<DraftDocumentTemplateListResponse>(
    matterGuid ? `/matter/${matterGuid}/draft-templates${query ? `?${query}` : ''}` : null,
    fetcher,
  );

  return {
    templates: data?.templates ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useAdminDraftDocumentTemplates = (
  options: { query?: string; limit?: number; enabled?: boolean; scopes?: string[] } = {},
) => {
  const params = new URLSearchParams();
  if (options.query) params.set('query', options.query);
  if (options.limit) params.set('limit', String(options.limit));
  for (const scope of options.scopes ?? []) {
    params.append('scope', scope);
  }
  const query = params.toString();
  const { data, error, isLoading, mutate } = useSWR<DraftDocumentTemplateListResponse>(
    options.enabled === false ? null : `/admin/draft-templates${query ? `?${query}` : ''}`,
    fetcher,
  );

  return {
    templates: data?.templates ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useDraftDocumentTemplateDocx = (
  matterGuid: string | undefined,
  templateGuid: string | undefined | null,
) => {
  const key = matterGuid && templateGuid
    ? `/matter/${matterGuid}/draft-templates/${templateGuid}/file#preview`
    : null;

  const fetchTemplateDocx = () => {
    if (!matterGuid || !templateGuid) {
      throw new Error('Draft template is unavailable');
    }
    return fetchDraftDocumentTemplateBuffer(matterGuid, templateGuid)
      .then(normalizeDraftDocumentDocxForPreview);
  };

  const { data, error, isLoading, mutate } = useSWR<ArrayBuffer>(key, fetchTemplateDocx);

  return {
    documentBuffer: data ?? null,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useDraftDocument = (
  matterGuid: string | undefined,
  documentGuid: string | undefined,
  options: { includeNodes?: boolean; refreshInterval?: number } = {},
) => {
  const query = options.includeNodes ? '?include_nodes=true' : '';
  const { data, error, isLoading, isValidating, mutate } = useSWR<DraftDocument>(
    matterGuid && documentGuid ? `/matter/${matterGuid}/drafts/${documentGuid}${query}` : null,
    fetcher,
    {
      errorRetryCount: 20,
      errorRetryInterval: 1000,
      refreshInterval: options.refreshInterval,
    },
  );

  return {
    draftDocument: data ?? null,
    isLoading,
    isValidating,
    isError: error,
    mutate,
  };
};

export const useDraftDocumentVersions = (
  matterGuid: string | undefined,
  documentGuid: string | undefined,
  options: { refreshInterval?: number } = {},
) => {
  const { data, error, isLoading, mutate } = useSWR<DraftDocumentVersionHistoryResponse>(
    matterGuid && documentGuid ? `/matter/${matterGuid}/drafts/${documentGuid}/versions` : null,
    fetcher,
    {
      refreshInterval: options.refreshInterval,
    },
  );

  return {
    versions: data?.versions ?? [],
    isLoading,
    isError: error,
    mutate,
  };
};

export const useDraftDocumentVersion = (
  matterGuid: string | undefined,
  documentGuid: string | undefined,
  versionNumber: number | null | undefined,
  options: { includeNodes?: boolean } = {},
) => {
  const query = options.includeNodes ? '?include_nodes=true' : '';
  const { data, error, isLoading, mutate } = useSWR<DraftDocumentVersionContent>(
    matterGuid && documentGuid && versionNumber
      ? `/matter/${matterGuid}/drafts/${documentGuid}/versions/${versionNumber}${query}`
      : null,
    fetcher,
  );

  return {
    version: data ?? null,
    isLoading,
    isError: error,
    mutate,
  };
};

export const useDraftDocumentDocx = (
  matterGuid: string | undefined,
  documentGuid: string | undefined,
  versionNumber: number | null | undefined,
  options: { normalizeForPreview?: boolean } = {},
) => {
  const normalizeForPreview = options.normalizeForPreview ?? true;
  const path = matterGuid && documentGuid && versionNumber
    ? `/matter/${matterGuid}/drafts/${documentGuid}/versions/${versionNumber}/docx`
    : null;
  const key = path ? `${path}#${normalizeForPreview ? 'preview' : 'raw'}` : null;

  const fetchDocx = () => {
    if (!matterGuid || !documentGuid || !versionNumber) {
      throw new Error('Draft document version is unavailable');
    }
    return fetchDraftDocumentDocxBuffer(matterGuid, documentGuid, versionNumber)
      .then(normalizeDraftDocumentDocxForEditor)
      .then((buffer) => (normalizeForPreview ? normalizeDraftDocumentDocxForPreview(buffer) : buffer));
  };

  const { data, error, isLoading, isValidating, mutate } = useSWR<ArrayBuffer>(
    key,
    fetchDocx,
    {
      errorRetryCount: 30,
      errorRetryInterval: 1000,
    },
  );

  return {
    documentBuffer: data ?? null,
    isLoading,
    isValidating,
    isError: error,
    mutate,
  };
};

export const useDraftDocumentCheckpointDocx = (
  matterGuid: string | undefined,
  documentGuid: string | undefined,
  checkpointGuid: string | null | undefined,
  options: { normalizeForPreview?: boolean } = {},
) => {
  const normalizeForPreview = options.normalizeForPreview ?? true;
  const path = matterGuid && documentGuid && checkpointGuid
    ? `/matter/${matterGuid}/drafts/${documentGuid}/working-copy/checkpoints/${checkpointGuid}/docx`
    : null;
  const key = path ? `${path}#${normalizeForPreview ? 'preview' : 'raw'}` : null;

  const fetchDocx = () => {
    if (!matterGuid || !documentGuid || !checkpointGuid) {
      throw new Error('Draft working-copy checkpoint is unavailable');
    }
    return fetchDraftDocumentCheckpointDocxBuffer(matterGuid, documentGuid, checkpointGuid)
      .then(normalizeDraftDocumentDocxForEditor)
      .then((buffer) => (normalizeForPreview ? normalizeDraftDocumentDocxForPreview(buffer) : buffer));
  };

  const { data, error, isLoading, isValidating, mutate } = useSWR<ArrayBuffer>(
    key,
    fetchDocx,
    {
      errorRetryCount: 30,
      errorRetryInterval: 1000,
    },
  );

  return {
    documentBuffer: data ?? null,
    isLoading,
    isValidating,
    isError: error,
    mutate,
  };
};

export const useDraftDocumentChangesDocx = (
  matterGuid: string | undefined,
  documentGuid: string | undefined,
  versionNumber: number | null | undefined,
) => {
  const key = matterGuid && documentGuid && versionNumber && versionNumber > 1
    ? `/matter/${matterGuid}/drafts/${documentGuid}/versions/${versionNumber}/changes-docx`
    : null;

  const fetchChangesDocx = () => {
    if (!matterGuid || !documentGuid || !versionNumber) {
      throw new Error('Draft document version is unavailable');
    }
    return fetchDraftDocumentChangesDocxBuffer(matterGuid, documentGuid, versionNumber)
      .then(normalizeDraftDocumentDocxForPreview);
  };

  const { data, error, isLoading, isValidating, mutate } = useSWR<ArrayBuffer>(
    key,
    fetchChangesDocx,
    {
      errorRetryCount: 30,
      errorRetryInterval: 1000,
    },
  );

  return {
    documentBuffer: data ?? null,
    isLoading,
    isValidating,
    isError: error,
    mutate,
  };
};

export async function createDraftDocument(
  matterGuid: string,
  request: CreateDraftDocumentRequest,
): Promise<DraftDocument> {
  const response = await backendClient.post(`/matter/${matterGuid}/drafts`, request);
  return response.data;
}

export async function renameDraftDocument(
  matterGuid: string,
  documentGuid: string,
  title: string,
): Promise<DraftDocument> {
  const response = await backendClient.patch(`/matter/${matterGuid}/drafts/${documentGuid}`, { title });
  return response.data;
}

export async function deleteDraftDocument(
  matterGuid: string,
  documentGuid: string,
): Promise<void> {
  await backendClient.delete(`/matter/${matterGuid}/drafts/${documentGuid}`);
}

export async function updateDraftDocumentVisibility(
  matterGuid: string,
  documentGuid: string,
  visibility: DraftDocumentVisibility,
): Promise<DraftDocument> {
  const response = await backendClient.patch(
    `/matter/${matterGuid}/drafts/${documentGuid}/visibility`,
    { visibility },
  );
  return response.data;
}

export function draftDocumentDownloadFilename(
  draftDocument: { file_name?: string | null; title?: string | null },
): string {
  const fileName = draftDocument.file_name || draftDocument.title || 'draft-document';
  const safeName = fileName
    .replace(/[^\w .-]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[ ._-]+|[ ._-]+$/g, '') || 'draft-document';
  return safeName;
}

function draftDocumentVersionFilename(title: string, versionNumber: number): string {
  const safeName = draftDocumentDownloadFilename({ file_name: title, title });
  const stem = safeName.replace(/\.docx$/i, '') || 'draft-document';
  return `${stem}-v${versionNumber}.docx`;
}

function draftDocumentMimeType(fileName: string): string {
  const extension = fileName.toLowerCase().split('.').pop();
  if (extension === 'docx') return DOCX_MIME_TYPE;
  if (extension === 'pdf') return 'application/pdf';
  if (extension === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  if (extension === 'xlsm') return 'application/vnd.ms-excel.sheet.macroEnabled.12';
  if (extension === 'xls') return 'application/vnd.ms-excel';
  if (extension === 'csv') return 'text/csv';
  if (extension === 'eml') return 'message/rfc822';
  if (extension === 'msg') return 'application/vnd.ms-outlook';
  return 'application/octet-stream';
}

export async function downloadDraftDocument(
  matterGuid: string,
  draftDocument: Pick<DraftDocument, 'guid' | 'file_name' | 'title'>,
): Promise<void> {
  const filename = draftDocumentDownloadFilename(draftDocument);
  const buffer = await fetchDraftDocumentFileBuffer(matterGuid, draftDocument.guid);
  downloadArrayBuffer(buffer, filename, draftDocumentMimeType(filename));
}

export async function downloadDraftDocumentDocx(
  matterGuid: string,
  documentGuid: string,
  versionNumber: number,
  title?: string | null,
): Promise<void> {
  const buffer = await fetchDraftDocumentDocxBuffer(matterGuid, documentGuid, versionNumber);
  const filenameBase = draftDocumentDownloadFilename({ file_name: title || 'draft-document', title });
  const versionedBase = filenameBase.replace(/\.docx$/i, '');
  const filename = `${versionedBase || 'draft-document'}-v${versionNumber}.docx`;
  downloadArrayBuffer(
    buffer,
    filename,
    DOCX_MIME_TYPE,
  );
}

export async function downloadDraftDocumentCheckpointDocx(
  matterGuid: string,
  documentGuid: string,
  checkpointGuid: string,
  draftDocument: Pick<DraftDocument, 'file_name' | 'title'>,
): Promise<void> {
  const buffer = await fetchDraftDocumentCheckpointDocxBuffer(matterGuid, documentGuid, checkpointGuid);
  const filenameBase = draftDocumentDownloadFilename(draftDocument);
  const stem = filenameBase.replace(/\.docx$/i, '') || 'draft-document';
  downloadArrayBuffer(
    buffer,
    `${stem}.docx`,
    DOCX_MIME_TYPE,
  );
}

export async function saveDraftDocumentDocx(
  matterGuid: string,
  documentGuid: string,
  baseVersionNumber: number,
  buffer: ArrayBuffer,
): Promise<DraftDocument> {
  const formData = new FormData();
  formData.append(
    'file',
    new Blob([new Uint8Array(buffer)], { type: DOCX_MIME_TYPE }),
    draftDocumentVersionFilename('draft-document', baseVersionNumber),
  );
  const response = await backendClient.post(
    `/matter/${matterGuid}/drafts/${documentGuid}/versions/${baseVersionNumber}/docx`,
    formData,
  );
  return response.data.document;
}

export async function checkpointDraftDocumentWorkingCopyDocx(
  matterGuid: string,
  documentGuid: string,
  baseVersionNumber: number,
  buffer: ArrayBuffer,
  checkpointReason = 'autosave',
): Promise<DraftDocumentWorkingCopyCheckpointResponse> {
  const formData = new FormData();
  formData.append('base_version_number', String(baseVersionNumber));
  formData.append('checkpoint_reason', checkpointReason);
  formData.append(
    'file',
    new Blob([new Uint8Array(buffer)], { type: DOCX_MIME_TYPE }),
    draftDocumentVersionFilename('draft-working-copy', baseVersionNumber),
  );
  const response = await backendClient.post(
    `/matter/${matterGuid}/drafts/${documentGuid}/working-copy/checkpoint`,
    formData,
  );
  return response.data;
}

export async function restoreDraftDocumentVersion(
  matterGuid: string,
  documentGuid: string,
  versionNumber: number,
): Promise<DraftDocumentRestoreVersionResponse> {
  const response = await backendClient.post(
    `/matter/${matterGuid}/drafts/${documentGuid}/versions/${versionNumber}/restore`,
  );
  return response.data;
}

export async function createDraftDocumentTemplate(
  matterGuid: string,
  request: CreateDraftDocumentTemplateRequest,
): Promise<DraftDocumentTemplate> {
  const formData = templateFormData(request);
  formData.append('scope', request.scope);
  const response = await backendClient.post(`/matter/${matterGuid}/draft-templates`, formData);
  return response.data;
}

export async function createAdminDraftDocumentTemplate(
  request: CreateDraftDocumentTemplateRequest,
): Promise<DraftDocumentTemplate> {
  const formData = templateFormData(request);
  formData.append('scope', request.scope);
  const response = await backendClient.post('/admin/draft-templates', formData);
  return response.data;
}

export async function updateDraftDocumentTemplate(
  matterGuid: string,
  templateGuid: string,
  request: UpdateDraftDocumentTemplateRequest,
): Promise<DraftDocumentTemplate> {
  const response = await backendClient.patch(
    `/matter/${matterGuid}/draft-templates/${templateGuid}`,
    request,
  );
  return response.data;
}

export async function updateAdminDraftDocumentTemplate(
  templateGuid: string,
  request: UpdateDraftDocumentTemplateRequest,
): Promise<DraftDocumentTemplate> {
  const response = await backendClient.patch(`/admin/draft-templates/${templateGuid}`, request);
  return response.data;
}

export async function deleteDraftDocumentTemplate(
  matterGuid: string,
  templateGuid: string,
): Promise<void> {
  await backendClient.delete(`/matter/${matterGuid}/draft-templates/${templateGuid}`);
}

export async function deleteAdminDraftDocumentTemplate(
  templateGuid: string,
): Promise<void> {
  await backendClient.delete(`/admin/draft-templates/${templateGuid}`);
}

export function draftDocumentTemplateDownloadFilename(
  template: Pick<DraftDocumentTemplate, 'file_name' | 'title'>,
): string {
  const fileName = template.file_name || `${template.title || 'template'}.docx`;
  const safeName = fileName
    .replace(/[^\w .-]+/g, '_')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[ ._-]+|[ ._-]+$/g, '') || 'template.docx';
  return safeName.toLowerCase().endsWith('.docx') ? safeName : `${safeName}.docx`;
}

export async function downloadDraftDocumentTemplate(
  matterGuid: string,
  template: DraftDocumentTemplate,
): Promise<void> {
  const buffer = await fetchDraftDocumentTemplateBuffer(matterGuid, template.template_guid);
  downloadArrayBuffer(buffer, draftDocumentTemplateDownloadFilename(template), DOCX_MIME_TYPE);
}

export async function downloadAdminDraftDocumentTemplate(
  template: DraftDocumentTemplate,
): Promise<void> {
  const buffer = await fetchAdminDraftDocumentTemplateBuffer(template.template_guid);
  downloadArrayBuffer(buffer, draftDocumentTemplateDownloadFilename(template), DOCX_MIME_TYPE);
}

export async function fetchDraftDocumentTemplateBuffer(
  matterGuid: string,
  templateGuid: string,
): Promise<ArrayBuffer> {
  return fetchArrayBuffer(
    `/matter/${matterGuid}/draft-templates/${templateGuid}/file`,
    'Failed to load draft template',
  );
}

export async function fetchAdminDraftDocumentTemplateBuffer(
  templateGuid: string,
): Promise<ArrayBuffer> {
  return fetchArrayBuffer(
    `/admin/draft-templates/${templateGuid}/file`,
    'Failed to load draft template',
  );
}

export async function fetchDraftDocumentFileBuffer(
  matterGuid: string,
  documentGuid: string,
): Promise<ArrayBuffer> {
  return fetchArrayBuffer(
    `/matter/${matterGuid}/drafts/${documentGuid}/file`,
    'Failed to load draft document',
  );
}

export async function fetchDraftDocumentDocxBuffer(
  matterGuid: string,
  documentGuid: string,
  versionNumber: number,
): Promise<ArrayBuffer> {
  return fetchArrayBuffer(
    `/matter/${matterGuid}/drafts/${documentGuid}/versions/${versionNumber}/docx`,
    'Failed to load draft document',
  );
}

export async function fetchDraftDocumentCheckpointDocxBuffer(
  matterGuid: string,
  documentGuid: string,
  checkpointGuid: string,
): Promise<ArrayBuffer> {
  return fetchArrayBuffer(
    `/matter/${matterGuid}/drafts/${documentGuid}/working-copy/checkpoints/${checkpointGuid}/docx`,
    'Failed to load draft working-copy checkpoint',
  );
}

export async function fetchDraftDocumentChangesDocxBuffer(
  matterGuid: string,
  documentGuid: string,
  versionNumber: number,
): Promise<ArrayBuffer> {
  return fetchArrayBuffer(
    `/matter/${matterGuid}/drafts/${documentGuid}/versions/${versionNumber}/changes-docx`,
    'Failed to load draft document changes',
  );
}

function templateFormData(
  request: Omit<CreateDraftDocumentTemplateRequest, 'scope'>,
): FormData {
  const formData = new FormData();
  formData.append('file', request.file);
  formData.append('title', request.title);
  if (request.description !== undefined && request.description !== null) {
    formData.append('description', request.description);
  }
  if (request.when_to_use !== undefined && request.when_to_use !== null) {
    formData.append('when_to_use', request.when_to_use);
  }
  if (request.availability_region !== undefined && request.availability_region !== null) {
    formData.append('availability_region', request.availability_region);
  }
  if (request.availability_forum !== undefined && request.availability_forum !== null) {
    formData.append('availability_forum', request.availability_forum);
  }
  return formData;
}

async function fetchArrayBuffer(path: string, fallbackMessage: string): Promise<ArrayBuffer> {
  try {
    const response = await backendClient.get(path, { responseType: 'arraybuffer' });
    return arrayBufferFromResponseData(response.data);
  } catch (error) {
    const data = (error as { response?: { data?: unknown } })?.response?.data;
    if (data instanceof ArrayBuffer) {
      try {
        const parsed = JSON.parse(new TextDecoder().decode(data));
        throw new Error(parsed.detail || parsed.message || fallbackMessage);
      } catch (decodeError) {
        if (!(decodeError instanceof SyntaxError)) {
          throw decodeError;
        }
      }
    }
    throw new Error(apiErrorMessage(error) || fallbackMessage);
  }
}

function arrayBufferFromResponseData(data: unknown): ArrayBuffer {
  if (data instanceof ArrayBuffer) {
    return data;
  }

  if (ArrayBuffer.isView(data)) {
    const copy = new Uint8Array(data.byteLength);
    copy.set(new Uint8Array(data.buffer, data.byteOffset, data.byteLength));
    return copy.buffer;
  }

  throw new Error('Response was not a binary file');
}

function downloadArrayBuffer(buffer: ArrayBuffer, filename: string, mimeType: string): void {
  const blob = new Blob([new Uint8Array(buffer)], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
